import type { PlayerAccountState } from '@league/shared/contract';

import { QUIET_ACTION } from '../../components/PlayerTable';

/**
 * SIGNING IN, as the roster and the substitute pool both show it. Ruled 2026-09-25: adding a player
 * sends her nothing; the director presses Invite. One component, so the two screens cannot describe
 * the same woman differently.
 */

/** What each sign-in state says on her row. Plain words, no colour: nothing here is a problem. */
export const ACCOUNT_LABEL: Record<PlayerAccountState, string> = {
  not_invited: 'Not invited to sign in',
  invited: 'Invited, not signed in yet',
  signed_in: 'Signs in',
  staff_login: 'Plays on their director login',
  no_account: 'No sign-in',
};

/**
 * THE ROW'S INVITE. "Invite" before anybody has; "Send again" after, because the link expires and a
 * player who lost the first one is the ordinary case. Nothing at all once she signs in, or when the
 * address is a staff login — there is nothing to send.
 *
 * The accessible name carries the person, as Remove's does: a screen reader otherwise hears a list
 * of identical "Invite" buttons.
 */
export function InviteButton({
  player,
  account,
  disabled,
  onInvite,
  invitedAt,
}: {
  player: { firstName: string; lastName: string };
  account: PlayerAccountState;
  disabled: boolean;
  onInvite: () => void;
  /** When she was last invited to this league, if she has been — the roster's own (2026-10-15). */
  invitedAt?: string | null | undefined;
}) {
  /* The roster passes `invitedAt`: anybody with a login can be invited to the league, and again. */
  if (invitedAt !== undefined) {
    if (account === 'staff_login' || account === 'no_account') return null;
  } else if (account !== 'not_invited' && account !== 'invited') return null;
  const again = invitedAt !== undefined ? invitedAt !== null : account === 'invited';
  return (
    /* A row action: the roster's quiet text and wording (2026-10-16). */
    <button
      type="button"
      className={`${QUIET_ACTION} hover:border-deep`}
      disabled={disabled}
      aria-label={`${again ? 'Resend invite to' : 'Invite'} ${player.firstName} ${player.lastName}`}
      onClick={onInvite}
    >
      {again ? 'Resend invite' : 'Invite'}
    </button>
  );
}
